import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ethers } from 'ethers';
import {
  Wallet, Check, Copy, AlertTriangle, ArrowRight,
  Shield, Award, Clock, Loader2, ExternalLink, ChevronRight, Link2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { KutroLogoFull } from '@/components/Logo';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Step Constants ─── */
const STEPS = { LOOKUP: 0, LINK_WALLET: 1, ELIGIBLE: 2, CLAIMING: 3, CLAIMED: 4 };

const TIER_COLORS = {
  'Agent-Zero': { bg: 'from-orange-500/10 to-amber-500/10', border: 'border-orange-500/20', text: 'text-orange-400', badge: 'bg-orange-500/10' },
  'Early Access': { bg: 'from-blue-500/10 to-cyan-500/10', border: 'border-blue-500/20', text: 'text-blue-400', badge: 'bg-blue-500/10' },
  'Standard': { bg: 'from-purple-500/10 to-indigo-500/10', border: 'border-purple-500/20', text: 'text-purple-400', badge: 'bg-purple-500/10' },
  'Ineligible': { bg: 'from-gray-500/10 to-gray-500/10', border: 'border-gray-500/20', text: 'text-gray-400', badge: 'bg-gray-500/10' },
};

export default function AirdropClaim() {
  const [step, setStep] = useState(STEPS.LOOKUP);
  const [agentId, setAgentId] = useState('');
  const [airdropData, setAirdropData] = useState(null);
  const [walletAddress, setWalletAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [claimTxHash, setClaimTxHash] = useState('');
  const [copied, setCopied] = useState(false);
  const [claims, setClaims] = useState(null);
  const [stats, setStats] = useState(null);

  // Load waitlist stats
  useEffect(() => {
    fetch(`${API}/api/waitlist/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  // Extract agent_id from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('agent');
    if (id) setAgentId(id);
  }, []);

  // Try to load claims.json (available after Merkle tree is generated)
  useEffect(() => {
    fetch('/airdrop/claims.json')
      .then(r => r.ok ? r.json() : null)
      .then(setClaims)
      .catch(() => setClaims(null));
  }, []);

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) {
      setError('MetaMask not detected. Please install MetaMask to continue.');
      return null;
    }
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      setWalletAddress(accounts[0]);
      return accounts[0];
    } catch {
      setError('Wallet connection declined.');
      return null;
    }
  }, []);

  const lookupAgent = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/waitlist/airdrop-status/${agentId.trim()}`);
      if (!res.ok) {
        try {
          const d = await res.json();
          setError(d.detail || 'Agent ID not found');
        } catch {
          setError('Agent ID not found');
        }
        return;
      }
      const data = await res.json();
      setAirdropData(data);

      if (data.wallet_linked) {
        setWalletAddress(data.wallet_address);
        setStep(STEPS.ELIGIBLE);
      } else {
        setStep(STEPS.LINK_WALLET);
      }
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  const linkWallet = async () => {
    setError('');
    setLoading(true);
    try {
      const addr = await connectWallet();
      if (!addr) { setLoading(false); return; }

      const res = await fetch(`${API}/api/waitlist/link-wallet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: agentId.trim(), wallet_address: addr }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Failed to link wallet');
        return;
      }
      setAirdropData(prev => ({ ...prev, wallet_linked: true, wallet_address: addr }));
      setStep(STEPS.ELIGIBLE);
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  };

  const claimTokens = async () => {
    if (!claims || !walletAddress) {
      setError('Claim data not available yet. The Merkle tree has not been generated.');
      return;
    }

    const userClaim = claims[walletAddress.toLowerCase()];
    if (!userClaim) {
      setError('Your wallet is not in the Merkle tree. Contact support.');
      return;
    }

    setStep(STEPS.CLAIMING);
    setError('');
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);

      // Ensure correct network (Base: 8453)
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== 8453) {
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x2105' }],
          });
        } catch {
          setError('Please switch to Base network in your wallet.');
          setStep(STEPS.ELIGIBLE);
          return;
        }
      }

      const signer = await provider.getSigner();
      const AirdropABI = (await import('@/config/KutroAirdropABI.json')).default;

      // Load deployment info
      let airdropAddress;
      try {
        const deployRes = await fetch('/airdrop/deployment.json');
        const deployData = await deployRes.json();
        airdropAddress = deployData.address;
      } catch {
        setError('Airdrop contract deployment info not found.');
        setStep(STEPS.ELIGIBLE);
        return;
      }

      const contract = new ethers.Contract(airdropAddress, AirdropABI, signer);

      // Check if already claimed
      const alreadyClaimed = await contract.isClaimed(userClaim.index);
      if (alreadyClaimed) {
        setError('You have already claimed your airdrop.');
        setStep(STEPS.CLAIMED);
        return;
      }

      // Execute claim
      const tx = await contract.claim(
        userClaim.index,
        userClaim.account,
        userClaim.amount,
        userClaim.proof
      );
      setClaimTxHash(tx.hash);
      await tx.wait();
      setStep(STEPS.CLAIMED);
    } catch (err) {
      console.error('Claim error:', err);
      setError(err.reason || err.message || 'Transaction failed.');
      setStep(STEPS.ELIGIBLE);
    }
  };

  const tierStyle = TIER_COLORS[airdropData?.tier] || TIER_COLORS['Ineligible'];

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 backdrop-blur-xl bg-[#030303]/80">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <KutroLogoFull size={32} />
          </Link>
          <span className="text-xs font-mono text-gray-600">AIRDROP_CLAIM_v1</span>
        </div>
      </nav>

      <div className="container mx-auto px-6 pt-28 pb-20 max-w-lg">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-500/20 bg-orange-500/5 mb-5">
            <Award className="w-4 h-4 text-orange-400" />
            <span className="text-xs font-mono text-orange-400 tracking-wider">KOJIRU 5,000 AIRDROP</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Claim Your KOJIRU</h1>
          <p className="text-gray-500 text-sm">Flat Fee Home Lending.</p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {['Verify', 'Link Wallet', 'Claim'].map((label, i) => {
            const active = step >= i * 2;
            return (
              <React.Fragment key={i}>
                {i > 0 && <div className={`w-8 h-px ${active ? 'bg-blue-500' : 'bg-white/10'}`} />}
                <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono ${active ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'text-gray-600 border border-white/5'}`}>
                  {active && step > i * 2 ? <Check className="w-3 h-3" /> : null}
                  {label}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Error Banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3"
            >
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-400">{error}</p>
              </div>
              <button onClick={() => setError('')} className="text-red-400/50 hover:text-red-400 text-xs">dismiss</button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── STEP 0: Agent ID Lookup ── */}
        {step === STEPS.LOOKUP && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <form onSubmit={lookupAgent} className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl p-8 space-y-5">
              <div className="text-center mb-2">
                <Shield className="w-10 h-10 text-blue-400 mx-auto mb-3" />
                <h3 className="font-semibold text-lg">Enter Your Agent ID</h3>
                <p className="text-xs text-gray-500 mt-1">The Agent ID you received when you joined the Kojiru 5,000.</p>
              </div>
              <div>
                <input
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value.toUpperCase())}
                  placeholder="AGT-XXXXXX"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-center text-lg font-mono tracking-widest focus:outline-none focus:border-blue-500/50 placeholder:text-gray-700"
                  required
                  data-testid="airdrop-agent-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:brightness-110 font-mono"
                data-testid="airdrop-lookup-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>VERIFY ELIGIBILITY <ChevronRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </form>
          </motion.div>
        )}

        {/* ── STEP 1: Link Wallet ── */}
        {step === STEPS.LINK_WALLET && airdropData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            {/* Allocation Card */}
            <div className={`rounded-2xl border ${tierStyle.border} bg-gradient-to-br ${tierStyle.bg} p-6 text-center`}>
              <span className={`inline-block text-xs font-mono px-3 py-1 rounded-full ${tierStyle.badge} ${tierStyle.text} border ${tierStyle.border} mb-3`}>
                {airdropData.tier} — Position #{airdropData.position}
              </span>
              <div className="text-3xl font-mono font-bold text-white mb-1">
                {airdropData.allocation_kutro.toLocaleString()} KOJIRU
              </div>
              <p className="text-sm text-gray-500">Your allocation</p>
            </div>

            {/* Connect Wallet CTA */}
            <div className="rounded-2xl border border-white/10 bg-black/40 p-8 text-center space-y-4">
              <Wallet className="w-10 h-10 text-purple-400 mx-auto" />
              <h3 className="font-semibold text-lg">Connect & Link Your Wallet</h3>
              <p className="text-sm text-gray-500">Link a Base network wallet to your Agent ID to claim your tokens.</p>
              <Button
                onClick={linkWallet}
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:brightness-110 font-mono"
                data-testid="airdrop-link-wallet-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wallet className="w-4 h-4 mr-2" /> CONNECT WALLET</>}
              </Button>
              <p className="text-[11px] text-gray-600">One wallet per Agent ID. This cannot be changed.</p>
            </div>
          </motion.div>
        )}

        {/* ── STEP 2: Eligible — Ready to Claim ── */}
        {step === STEPS.ELIGIBLE && airdropData && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            {/* Allocation Card */}
            <div className={`rounded-2xl border ${tierStyle.border} bg-gradient-to-br ${tierStyle.bg} p-6 text-center`}>
              <span className={`inline-block text-xs font-mono px-3 py-1 rounded-full ${tierStyle.badge} ${tierStyle.text} border ${tierStyle.border} mb-3`}>
                {airdropData.tier} — Position #{airdropData.position}
              </span>
              <div className="text-3xl font-mono font-bold text-white mb-1">
                {airdropData.allocation_kutro.toLocaleString()} KOJIRU
              </div>
              <p className="text-sm text-gray-500">Ready to claim</p>
            </div>

            {/* Wallet Info */}
            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-green-400" />
                  <span className="text-xs font-mono text-gray-500">Linked Wallet</span>
                </div>
                <code className="text-xs font-mono text-gray-400">
                  {walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}
                </code>
              </div>
            </div>

            {/* Claim Button */}
            <Button
              onClick={claimTokens}
              className="w-full h-14 bg-gradient-to-r from-orange-500 to-amber-500 hover:brightness-110 text-lg font-mono"
              data-testid="airdrop-claim-btn"
            >
              CLAIM {airdropData.allocation_kutro.toLocaleString()} KOJIRU <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {!claims && (
              <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-center">
                <Clock className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
                <p className="text-sm text-yellow-400">Claim period has not started yet.</p>
                <p className="text-xs text-gray-500 mt-1">Your wallet is linked and your allocation is secured. You'll be notified when claiming opens.</p>
              </div>
            )}
          </motion.div>
        )}

        {/* ── STEP 3: Claiming (Transaction In Progress) ── */}
        {step === STEPS.CLAIMING && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border border-blue-500/20 bg-blue-500/5 p-10 text-center">
            <Loader2 className="w-12 h-12 text-blue-400 mx-auto mb-4 animate-spin" />
            <h3 className="text-xl font-bold mb-2">Claiming Your KOJIRU...</h3>
            <p className="text-sm text-gray-400 mb-4">Please confirm the transaction in your wallet and wait for it to be mined on Base.</p>
            {claimTxHash && (
              <a
                href={`https://basescan.org/tx/${claimTxHash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-mono text-blue-400 hover:underline"
              >
                View on Basescan <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </motion.div>
        )}

        {/* ── STEP 4: Claimed! ── */}
        {step === STEPS.CLAIMED && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-5">
            <div className="rounded-2xl border border-green-500/20 bg-green-500/5 p-10 text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-2xl font-bold mb-2">KOJIRU Claimed!</h3>
              <p className="text-gray-400 text-sm mb-4">
                {airdropData?.allocation_kutro.toLocaleString()} KOJIRU has been sent to your wallet.
              </p>
              {claimTxHash && (
                <a
                  href={`https://basescan.org/tx/${claimTxHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm font-mono text-blue-400 hover:bg-white/10 transition-colors"
                  data-testid="claim-tx-link"
                >
                  <ExternalLink className="w-4 h-4" /> View Transaction
                </a>
              )}
            </div>

            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-5 text-center">
              <p className="text-sm text-gray-400 mb-3">What's next?</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/staking" className="flex-1 px-4 py-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm font-medium text-blue-400 hover:bg-blue-500/20 transition-colors text-center">
                  Stake Your KOJIRU
                </Link>
                <Link to="/governance" className="flex-1 px-4 py-3 rounded-lg bg-purple-500/10 border border-purple-500/20 text-sm font-medium text-purple-400 hover:bg-purple-500/20 transition-colors text-center">
                  Vote on Proposals
                </Link>
              </div>
            </div>

            <p className="text-center text-xs text-gray-600">Flat Fee Home Lending.</p>
          </motion.div>
        )}

        {/* Stats Footer */}
        {stats && (
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-6 text-xs font-mono text-gray-600">
              <span>{stats.total_signups.toLocaleString()} agents deployed</span>
              <span>|</span>
              <span>{stats.spots_remaining.toLocaleString()} spots left</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
